import { DashboardViewer } from '@/components/dashboard/DashboardViewer';
import { ApiError } from '@/lib/api';
import { meQueryOptions } from '@/lib/auth';
import { BrowserFaviconSync, BrowserTitleSync } from '@/lib/branding';
import { queryClient } from '@/lib/queryClient';
import { Home } from '@/routes/Home';
import { Licensing } from '@/routes/Licensing';
import { Login } from '@/routes/Login';
import { Onboarding } from '@/routes/Onboarding';
import { UserApiKeys } from '@/routes/UserApiKeys';
import { UserGroups } from '@/routes/UserGroups';
import { AdminApiKeys } from '@/routes/admin/ApiKeys';
import { AdminApiKeysUsage } from '@/routes/admin/ApiKeysUsage';
import { AdminAuditLogs } from '@/routes/admin/AuditLogs';
import { AdminAuth } from '@/routes/admin/Auth';
import { AdminBannedKeywords } from '@/routes/admin/BannedKeywords';
import { AdminBrandingPage } from '@/routes/admin/Branding';
import { AdminChatLog } from '@/routes/admin/ChatLog';
import { AdminChatSessionDetail } from '@/routes/admin/ChatSessionDetail';
import { AdminDashboard } from '@/routes/admin/Dashboard';
import { AdminFileStorage } from '@/routes/admin/FileStorage';
import { AdminFileUploads } from '@/routes/admin/FileUploads';
import { AdminLayout } from '@/routes/admin/Layout';
import { AdminLicenses } from '@/routes/admin/Licenses';
import { AdminResourceHosts } from '@/routes/admin/ResourceHosts';
import { AdminResourceUsage } from '@/routes/admin/ResourceUsage';
import { AdminRoles } from '@/routes/admin/Roles';
import { AdminSavedQueries } from '@/routes/admin/SavedQueries';
import { AdminServerJobs } from '@/routes/admin/ServerJobs';
import { AdminSmtp } from '@/routes/admin/Smtp';
import { AdminUsageStats } from '@/routes/admin/UsageStats';
import { AdminUserSync } from '@/routes/admin/UserSync';
import { AdminUsers } from '@/routes/admin/Users';
import { AdminAgentDetail } from '@/routes/admin/agents/Detail';
import { AdminAgentsList } from '@/routes/admin/agents/List';
import { AdminAgentNew } from '@/routes/admin/agents/New';
import { AdminDataSourceAppendExcel } from '@/routes/admin/data-sources/AppendExcel';
import { AdminDataSourceDetail } from '@/routes/admin/data-sources/Detail';
import { AdminDataSourceEdit } from '@/routes/admin/data-sources/Edit';
import { AdminDataSourcesList } from '@/routes/admin/data-sources/List';
import { AdminDataSourceNew } from '@/routes/admin/data-sources/New';
import { AdminDataSourceNewBigQuery } from '@/routes/admin/data-sources/NewBigQuery';
import { AdminDataSourceNewExcel } from '@/routes/admin/data-sources/NewExcel';
import { AdminDataSourceNewTableau } from '@/routes/admin/data-sources/NewTableau';
import { AdminFamilySiteDetail } from '@/routes/admin/family-sites/Detail';
import { AdminFamilySitesList } from '@/routes/admin/family-sites/List';
import { AdminHttpEndpointDetail } from '@/routes/admin/http-endpoints/Detail';
import { AdminHttpEndpointsList } from '@/routes/admin/http-endpoints/List';
import { AdminLlmBondingDetail } from '@/routes/admin/llm/BondingDetail';
import { AdminLlmBondings } from '@/routes/admin/llm/Bondings';
import { AdminLlmEndpointDetail } from '@/routes/admin/llm/EndpointDetail';
import { AdminLlmEndpoints } from '@/routes/admin/llm/Endpoints';
import { AdminPermissionsAttributes } from '@/routes/admin/permissions/Attributes';
import { AdminPermissionsRules } from '@/routes/admin/permissions/Rules';
import { AdminResponseControlPrompts } from '@/routes/admin/response-controls/Prompts';
import { AdminRoleDetail } from '@/routes/admin/roles/Detail';
import { AdminSkillLessonDetail } from '@/routes/admin/skill-lessons/Detail';
import { AdminSkillLessonsList } from '@/routes/admin/skill-lessons/List';
import { AdminSkillCreateWithAi } from '@/routes/admin/skills/CreateWithAi';
import { AdminSkillDetail } from '@/routes/admin/skills/Detail';
import { AdminSkillsList } from '@/routes/admin/skills/List';
import { AdminSlidesConfig } from '@/routes/admin/slides/Config';
import { AdminSystemPromptDetail } from '@/routes/admin/system-prompts/Detail';
import { AdminSystemPromptEdit } from '@/routes/admin/system-prompts/Edit';
import { AdminSystemPromptsList } from '@/routes/admin/system-prompts/List';
import { AdminUserDetail } from '@/routes/admin/users/Detail';
import { AdminWikiCategories } from '@/routes/admin/wiki/Categories';
import { AdminWikiConfig } from '@/routes/admin/wiki/Config';
import { AdminWikiDashboard } from '@/routes/admin/wiki/Dashboard';
import { AdminWikiDocumentPreview } from '@/routes/admin/wiki/DocumentPreview';
import { AdminWikiDocuments } from '@/routes/admin/wiki/Documents';
import { AdminWikiExternalSyncLogs } from '@/routes/admin/wiki/ExternalSyncLogs';
import { AdminWikiHealthCheck } from '@/routes/admin/wiki/HealthCheck';
import { AdminWikiPages } from '@/routes/admin/wiki/Pages';
import { ChatIndex } from '@/routes/chat/Index';
import { ChatLayout } from '@/routes/chat/Layout';
import { ChatSession } from '@/routes/chat/Session';
import { DashboardsIndex } from '@/routes/dashboard/Index';
import { SlidesEditor } from '@/routes/slides/Editor';
import { SlidesIndex } from '@/routes/slides/Index';
import { SlidesPresent } from '@/routes/slides/Present';
import { WikiCategoryPages } from '@/routes/wiki/CategoryPages';
import { WikiDocumentPreview } from '@/routes/wiki/DocumentPreview';
import { WikiDocuments } from '@/routes/wiki/Documents';
import { WikiGraph } from '@/routes/wiki/Graph';
import { WikiIndex } from '@/routes/wiki/Index';
import { WikiLayout } from '@/routes/wiki/Layout';
import { WikiLog } from '@/routes/wiki/Log';
import { WikiPageView } from '@/routes/wiki/Page';
import { WikiSearch } from '@/routes/wiki/Search';
import { WikiUpload } from '@/routes/wiki/Upload';
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';

const rootRoute = createRootRoute({
  component: () => (
    <>
      <BrowserTitleSync />
      <BrowserFaviconSync />
      <Outlet />
    </>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Home,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
});

/** 관리자 break-glass URL — 일반 /login 에 링크 노출 안 함. 운영자만 URL 알면 됨. */
const loginAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/admin',
  component: () => <Login variant="admin" />,
});

const licensingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/licensing',
  component: Licensing,
});

const chatLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat',
  component: ChatLayout,
});

const chatIndexRoute = createRoute({
  getParentRoute: () => chatLayoutRoute,
  path: '/',
  component: ChatIndex,
});

const ChatSessionSearchSchema = z.object({
  artifact: z.string().uuid().optional(),
});

function ChatSessionErrorComponent({ error }: { error: Error }) {
  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="mx-auto max-w-md py-12 text-center">
          <h1 className="mb-3 text-2xl font-semibold">채팅을 찾을 수 없습니다</h1>
          <p className="text-foreground-muted">
            요청하신 채팅이 존재하지 않거나 이미 삭제되었습니다.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex-1 flex items-center justify-center px-6">
      <p className="text-destructive text-sm">채팅을 불러오지 못했습니다</p>
    </div>
  );
}

export const chatSessionRoute = createRoute({
  getParentRoute: () => chatLayoutRoute,
  path: '$sessionId',
  component: ChatSessionRouteComponent,
  errorComponent: ChatSessionErrorComponent,
  validateSearch: ChatSessionSearchSchema,
});

function ChatSessionRouteComponent() {
  const { sessionId } = chatSessionRoute.useParams();
  return <ChatSession sessionId={sessionId} />;
}

const userApiKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/api-keys',
  component: UserApiKeys,
});

const userGroupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/user-groups',
  component: UserGroups,
});

const dashboardViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/view/$dashboardId',
  component: DashboardViewRouteComponent,
});

function DashboardViewRouteComponent() {
  const { dashboardId } = dashboardViewRoute.useParams();
  return <DashboardViewer dashboardId={dashboardId} />;
}

/**
 * /dashboards 목록 — ChatLayout 를 재사용해 사이드바 유지. 단수 /dashboard/view 와 충돌하지 않도록 복수형.
 */
const dashboardsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboards',
  component: ChatLayout,
});

const dashboardsIndexRoute = createRoute({
  getParentRoute: () => dashboardsLayoutRoute,
  path: '/',
  component: DashboardsIndex,
  validateSearch: (search: Record<string, unknown>) => ({
    tab: search.tab === 'shared' ? ('shared' as const) : ('mine' as const),
  }),
});

const OnboardingStepParam = z.enum(['db', 'admin', 'llm', 'storage', 'finish']);

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/$step',
  parseParams: (params) => ({
    step: OnboardingStepParam.parse(params.step),
  }),
  beforeLoad: ({ params }) => {
    const result = OnboardingStepParam.safeParse(params.step);
    if (!result.success) throw redirect({ to: '/' });
  },
  component: OnboardingRouteComponent,
});

function OnboardingRouteComponent() {
  const { step } = onboardingRoute.useParams();
  return <Onboarding step={step} />;
}

const slidesLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/slides',
  component: ChatLayout,
});

const slidesIndexRoute = createRoute({
  getParentRoute: () => slidesLayoutRoute,
  path: '/',
  component: SlidesIndex,
});

/**
 * 편집기는 사이드바 없이 풀 폭으로 — 부모를 rootRoute 로 직접 두어 ChatLayout 의 사이드바를 우회.
 * /slides/:id 는 자체 헤더에서 "← 슬라이드 목록" 으로 /slides 복귀.
 */
const slidesEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/slides/$slideId',
  component: SlidesEditorRouteComponent,
});

function SlidesEditorRouteComponent() {
  const { slideId } = slidesEditorRoute.useParams();
  return <SlidesEditor slideId={slideId} />;
}

/** 발표 모드 — 풀스크린, 사이드바·헤더 없음. */
const slidesPresentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/slides/$slideId/present',
  component: SlidesPresentRouteComponent,
});

function SlidesPresentRouteComponent() {
  const { slideId } = slidesPresentRoute.useParams();
  return <SlidesPresent slideId={slideId} />;
}

const wikiLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/wikis',
  // mount 이전에 인증 확정 — 캐시에 user 가 있으면 즉시 진입, 없으면 ensureQueryData
  // 가 /api/auth/me 한 번 await 한 뒤 결과로 판단. 컴포넌트 useEffect 로 늦게 튕기던
  // race (캐시 stale null 한 tick → /login → /chat 메인 튕김) 가 사라진다.
  beforeLoad: async () => {
    const user = await queryClient.ensureQueryData(meQueryOptions);
    if (!user) throw redirect({ to: '/login' });
  },
  component: WikiLayout,
});

const wikiIndexRoute = createRoute({
  getParentRoute: () => wikiLayoutRoute,
  path: '/',
  component: WikiIndex,
});

const wikiSearchRoute = createRoute({
  getParentRoute: () => wikiLayoutRoute,
  path: 'search',
  component: WikiSearch,
});

const wikiGraphRoute = createRoute({
  getParentRoute: () => wikiLayoutRoute,
  path: 'graph',
  component: WikiGraph,
});

const wikiLogRoute = createRoute({
  getParentRoute: () => wikiLayoutRoute,
  path: 'log',
  component: WikiLog,
});

const wikiDocumentsRoute = createRoute({
  getParentRoute: () => wikiLayoutRoute,
  path: 'documents',
  component: WikiDocuments,
});

const wikiDocumentPreviewRoute = createRoute({
  getParentRoute: () => wikiLayoutRoute,
  path: 'documents/$id/preview',
  component: WikiDocumentPreviewRouteComponent,
});

function WikiDocumentPreviewRouteComponent() {
  const { id } = wikiDocumentPreviewRoute.useParams();
  return <WikiDocumentPreview documentId={id} backTo="/wikis/documents" />;
}

const wikiUploadRoute = createRoute({
  getParentRoute: () => wikiLayoutRoute,
  path: 'upload',
  component: WikiUpload,
});

const wikiCategoryRoute = createRoute({
  getParentRoute: () => wikiLayoutRoute,
  path: 'c/$slug',
  component: WikiCategoryRouteComponent,
});

function WikiCategoryRouteComponent() {
  const { slug } = wikiCategoryRoute.useParams();
  return <WikiCategoryPages categorySlug={slug} />;
}

const wikiPageRoute = createRoute({
  getParentRoute: () => wikiLayoutRoute,
  path: 'p/$slug',
  component: WikiPageRouteComponent,
});

function WikiPageRouteComponent() {
  const { slug } = wikiPageRoute.useParams();
  return <WikiPageView slug={slug} />;
}

const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminLayout,
});

const adminDashboardRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/',
  component: AdminDashboard,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'users',
  component: AdminUsers,
});

const adminUserDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'users/$id',
  component: AdminUserDetailRouteComponent,
});

function AdminUserDetailRouteComponent() {
  const { id } = adminUserDetailRoute.useParams();
  return <AdminUserDetail userId={id} />;
}

const adminUserSyncRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'user-sync',
  component: AdminUserSync,
});

/** /admin/llm 직접 접근 시 엔드포인트 페이지로 redirect. */
const adminLlmIndexRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'llm',
  beforeLoad: () => {
    throw redirect({ to: '/admin/llm/endpoints' });
  },
});

const adminLlmEndpointsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'llm/endpoints',
  component: AdminLlmEndpoints,
});

const adminLlmEndpointDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'llm/endpoints/$id',
  component: AdminLlmEndpointDetailRouteComponent,
});

function AdminLlmEndpointDetailRouteComponent() {
  const { id } = adminLlmEndpointDetailRoute.useParams();
  return <AdminLlmEndpointDetail id={id} />;
}

const adminLlmBondingsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'llm/bondings',
  component: AdminLlmBondings,
});

const adminLlmBondingDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'llm/bondings/$id',
  component: AdminLlmBondingDetailRouteComponent,
});

function AdminLlmBondingDetailRouteComponent() {
  const { id } = adminLlmBondingDetailRoute.useParams();
  return <AdminLlmBondingDetail id={id} />;
}

const adminDataSourcesListRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'data-sources',
  component: AdminDataSourcesList,
});

const adminDataSourceNewRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'data-sources/new',
  component: AdminDataSourceNew,
});

const adminDataSourceNewExcelRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'data-sources/new-excel',
  component: AdminDataSourceNewExcel,
});

const adminDataSourceNewTableauRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'data-sources/new-tableau',
  component: AdminDataSourceNewTableau,
});

const adminDataSourceNewBigQueryRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'data-sources/new-bigquery',
  component: AdminDataSourceNewBigQuery,
});

const adminDataSourceDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'data-sources/$id',
  component: AdminDataSourceDetailRouteComponent,
});

function AdminDataSourceDetailRouteComponent() {
  const { id } = adminDataSourceDetailRoute.useParams();
  return <AdminDataSourceDetail id={id} />;
}

const adminDataSourceEditRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'data-sources/$id/edit',
  component: AdminDataSourceEditRouteComponent,
});

function AdminDataSourceEditRouteComponent() {
  const { id } = adminDataSourceEditRoute.useParams();
  return <AdminDataSourceEdit id={id} />;
}

const adminDataSourceAppendExcelRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'data-sources/$id/append-excel',
  component: AdminDataSourceAppendExcelRouteComponent,
});

function AdminDataSourceAppendExcelRouteComponent() {
  const { id } = adminDataSourceAppendExcelRoute.useParams();
  return <AdminDataSourceAppendExcel id={id} />;
}

const adminHttpEndpointsListRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'http-endpoints',
  component: AdminHttpEndpointsList,
});

const adminHttpEndpointDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'http-endpoints/$id',
  component: AdminHttpEndpointDetailRouteComponent,
});

function AdminHttpEndpointDetailRouteComponent() {
  const { id } = adminHttpEndpointDetailRoute.useParams();
  return <AdminHttpEndpointDetail id={id} />;
}

const adminBannedKeywordsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'banned-keywords',
  component: AdminBannedKeywords,
});

const adminFamilySitesListRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'family-sites',
  component: AdminFamilySitesList,
});

const adminFamilySiteDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'family-sites/$id',
  component: AdminFamilySiteDetailRouteComponent,
});

function AdminFamilySiteDetailRouteComponent() {
  const { id } = adminFamilySiteDetailRoute.useParams();
  return <AdminFamilySiteDetail id={id} />;
}

const adminSkillsListRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'skills',
  component: AdminSkillsList,
});

const adminSkillCreateWithAiRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  // 'skills/$id' 보다 먼저 선언해서 'create-with-ai' 가 $id 로 흡수되지 않도록 한다.
  path: 'skills/create-with-ai',
  component: AdminSkillCreateWithAi,
});

const adminSkillDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'skills/$id',
  component: AdminSkillDetailRouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    dataSourceId:
      typeof search.dataSourceId === 'string' && search.dataSourceId.length > 0
        ? search.dataSourceId
        : undefined,
  }),
});

function AdminSkillDetailRouteComponent() {
  const { id } = adminSkillDetailRoute.useParams();
  const { dataSourceId } = adminSkillDetailRoute.useSearch();
  return <AdminSkillDetail id={id} prefillDataSourceId={dataSourceId} />;
}

const adminAgentsListRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'agents',
  component: AdminAgentsList,
});

// 정적 세그먼트 'new' 가 동적 ':id' 보다 먼저 매칭되도록 선언 순서 유지.
const adminAgentNewRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'agents/new',
  component: AdminAgentNew,
});

const adminAgentDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'agents/$id',
  component: AdminAgentDetail,
});

const adminLicensesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'licenses',
  component: AdminLicenses,
});

const adminAuditLogsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'audit',
  validateSearch: (search: Record<string, unknown>) => ({
    status: search.status === 'error' || search.status === 'ok' ? search.status : undefined,
    action: typeof search.action === 'string' ? search.action : undefined,
  }),
  component: AdminAuditLogs,
});

const adminAuthRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'auth',
  component: AdminAuth,
});

const adminSmtpRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'smtp',
  component: AdminSmtp,
});

const adminBrandingRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'branding',
  component: AdminBrandingPage,
});

// 'api-keys/usage' 가 'api-keys/$id' 같은 동적 세그먼트보다 먼저 매칭되어야 하지만,
// 현재 api-keys 는 동적 세그먼트가 없으므로 순서 자유.
const adminApiKeysUsageRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'api-keys/usage',
  component: AdminApiKeysUsage,
});

const adminApiKeysRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'api-keys',
  component: AdminApiKeys,
});

const adminSavedQueriesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'saved-queries',
  component: AdminSavedQueries,
});

const adminFileUploadsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'file-uploads',
  component: AdminFileUploads,
});

const adminFileStorageRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'file-storage',
  component: AdminFileStorage,
});

export const adminChatSessionDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'chat-sessions/$id',
  component: AdminChatSessionDetailRouteComponent,
});

function AdminChatSessionDetailRouteComponent() {
  const { id } = adminChatSessionDetailRoute.useParams();
  return <AdminChatSessionDetail id={id} />;
}

/**
 * 외부 페이지(첨부파일·SavedQueries 등)에서 deep-link 로 진입할 때 사용.
 * sessionId 를 검색·자동선택, date 기준으로 -30일 ~ 해당일 범위로 필터 설정.
 * 다른 고급 필터는 default 로 리셋된다 (ChatLog 의 useEffect 가 처리).
 */
const AdminChatLogSearchSchema = z.object({
  sessionId: z.string().uuid().optional(),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

const adminChatLogRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'chat-log',
  component: AdminChatLog,
  validateSearch: AdminChatLogSearchSchema,
});

const adminUsageStatsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'usage-stats',
  component: AdminUsageStats,
});

const adminServerJobsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'server-jobs',
  component: AdminServerJobs,
});

const adminResourceUsageRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'resource-usage',
  component: AdminResourceUsage,
});

const adminResourceHostsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'resource-hosts',
  component: AdminResourceHosts,
});

const adminSystemPromptsListRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'system-prompts',
  component: AdminSystemPromptsList,
});

const adminSystemPromptDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'system-prompts/$key',
  component: AdminSystemPromptDetailRouteComponent,
});

function AdminSystemPromptDetailRouteComponent() {
  const { key } = adminSystemPromptDetailRoute.useParams();
  return <AdminSystemPromptDetail promptKey={key} />;
}

const adminSystemPromptEditRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'system-prompts/$key/edit',
  component: AdminSystemPromptEditRouteComponent,
});

function AdminSystemPromptEditRouteComponent() {
  const { key } = adminSystemPromptEditRoute.useParams();
  return <AdminSystemPromptEdit promptKey={key} />;
}

const adminWikiDashboardRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'wikis',
  component: AdminWikiDashboard,
});

const adminWikiConfigRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'wikis/config',
  component: AdminWikiConfig,
});

const adminWikiCategoriesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'wikis/categories',
  component: AdminWikiCategories,
});

const adminWikiDocumentsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'wikis/documents',
  component: AdminWikiDocuments,
});

const adminWikiDocumentPreviewRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'wikis/documents/$id/preview',
  component: AdminWikiDocumentPreviewRouteComponent,
});

function AdminWikiDocumentPreviewRouteComponent() {
  const { id } = adminWikiDocumentPreviewRoute.useParams();
  return <AdminWikiDocumentPreview documentId={id} />;
}

const adminWikiPagesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'wikis/pages',
  component: AdminWikiPages,
});

const adminWikiHealthCheckRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'wikis/health-check',
  component: AdminWikiHealthCheck,
});

const adminWikiExternalSyncLogsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'wikis/external-sync-logs',
  component: AdminWikiExternalSyncLogs,
});

const adminSlidesConfigRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'slides/config',
  component: AdminSlidesConfig,
});

const adminResponseControlPromptsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'response-controls/prompts',
  component: AdminResponseControlPrompts,
});

const adminSkillLessonsListRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'skill-lessons',
  component: AdminSkillLessonsList,
});

const adminRolesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'roles',
  component: AdminRoles,
});

const adminRoleDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'roles/$id',
  component: AdminRoleDetailRouteComponent,
});

function AdminRoleDetailRouteComponent() {
  const { id } = adminRoleDetailRoute.useParams();
  return <AdminRoleDetail roleId={id} />;
}

const adminPermissionsAttributesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'permissions/attributes',
  component: AdminPermissionsAttributes,
});

const adminPermissionsRulesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'permissions/rules',
  component: AdminPermissionsRules,
});

const adminSkillLessonDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'skill-lessons/$id',
  component: AdminSkillLessonDetailRouteComponent,
});

function AdminSkillLessonDetailRouteComponent() {
  const { id } = adminSkillLessonDetailRoute.useParams();
  return <AdminSkillLessonDetail id={id} />;
}

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  loginAdminRoute,
  licensingRoute,
  userApiKeysRoute,
  userGroupsRoute,
  dashboardViewRoute,
  dashboardsLayoutRoute.addChildren([dashboardsIndexRoute]),
  chatLayoutRoute.addChildren([chatIndexRoute, chatSessionRoute]),
  slidesLayoutRoute.addChildren([slidesIndexRoute]),
  slidesEditorRoute,
  slidesPresentRoute,
  wikiLayoutRoute.addChildren([
    wikiIndexRoute,
    wikiGraphRoute,
    wikiLogRoute,
    wikiSearchRoute,
    wikiDocumentsRoute,
    wikiDocumentPreviewRoute,
    wikiUploadRoute,
    wikiCategoryRoute,
    wikiPageRoute,
  ]),
  onboardingRoute,
  adminLayoutRoute.addChildren([
    adminDashboardRoute,
    adminUsersRoute,
    adminUserDetailRoute,
    adminUserSyncRoute,
    adminRolesRoute,
    adminRoleDetailRoute,
    adminPermissionsAttributesRoute,
    adminPermissionsRulesRoute,
    adminLlmIndexRoute,
    adminLlmEndpointsRoute,
    adminLlmEndpointDetailRoute,
    adminLlmBondingsRoute,
    adminLlmBondingDetailRoute,
    adminDataSourcesListRoute,
    adminDataSourceNewRoute,
    adminDataSourceNewExcelRoute,
    adminDataSourceNewTableauRoute,
    adminDataSourceNewBigQueryRoute,
    adminDataSourceDetailRoute,
    adminDataSourceEditRoute,
    adminDataSourceAppendExcelRoute,
    adminHttpEndpointsListRoute,
    adminHttpEndpointDetailRoute,
    adminBannedKeywordsRoute,
    adminFamilySitesListRoute,
    adminFamilySiteDetailRoute,
    adminSkillsListRoute,
    adminSkillCreateWithAiRoute,
    adminSkillDetailRoute,
    adminAgentsListRoute,
    adminAgentNewRoute,
    adminAgentDetailRoute,
    adminSkillLessonsListRoute,
    adminSkillLessonDetailRoute,
    adminWikiDashboardRoute,
    adminWikiConfigRoute,
    adminWikiCategoriesRoute,
    adminWikiDocumentsRoute,
    adminWikiDocumentPreviewRoute,
    adminWikiPagesRoute,
    adminWikiHealthCheckRoute,
    adminWikiExternalSyncLogsRoute,
    adminSlidesConfigRoute,
    adminSystemPromptsListRoute,
    adminSystemPromptDetailRoute,
    adminSystemPromptEditRoute,
    adminResponseControlPromptsRoute,
    adminAuditLogsRoute,
    adminLicensesRoute,
    adminAuthRoute,
    adminSmtpRoute,
    adminBrandingRoute,
    adminApiKeysUsageRoute,
    adminApiKeysRoute,
    adminSavedQueriesRoute,
    adminFileUploadsRoute,
    adminFileStorageRoute,
    adminChatSessionDetailRoute,
    adminChatLogRoute,
    adminUsageStatsRoute,
    adminServerJobsRoute,
    adminResourceUsageRoute,
    adminResourceHostsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter() {
  return <RouterProvider router={router} />;
}
